import { cloudStorage } from './cloudAppwriteClient';
import { databases, ensureAuth } from './appwriteClient';
import { ID, Query, Client } from 'appwrite';

const DATABASE_ID = '682acd26001f69683cd8';
const CLASS_ANNOUNCEMENTS_COLLECTION_ID = 'class-broadcast'; // You must create this collection in Appwrite

const BUCKET_ID = '6862996c001bd6436274';

const client = new Client()
  .setEndpoint('https://fra.cloud.appwrite.io/v1')
  .setProject('683566a7000250cec655');

export type Level = '1' | '2' | '3' | '4';

export interface ClassAnnouncement {
  $id: string;
  message: string;
  img?: string;
  time?: string;
  level: Level;
}

export interface FetchAnnouncementsResult {
  documents: any[];
  lastId?: string;
  hasMore: boolean;
}

/**
 * Fetch class announcements with pagination support.
 * Returns documents ordered by $createdAt desc (newest first), plus lastId (id of last doc in page)
 * and hasMore indicating whether there may be more documents to fetch.
 *
 * Usage notes:
 * - To load the newest page: call without cursorAfter. The returned documents are newest->oldest.
 * - To load older pages: call with cursorAfter equal to the lastId returned from the previous call.
 */
export const fetchClassAnnouncements = async (level: Level, limit = 20, cursorAfter?: string): Promise<FetchAnnouncementsResult> => {
  try {
    await ensureAuth();
    const queries: any[] = [Query.equal('level', level), Query.orderDesc('$createdAt'), Query.limit(limit)];
    if (cursorAfter) {
      queries.push(Query.cursorAfter(cursorAfter));
    }
    const response = await databases.listDocuments(DATABASE_ID, CLASS_ANNOUNCEMENTS_COLLECTION_ID, queries);
    const docs = response.documents || [];
    const lastId = docs.length ? docs[docs.length - 1].$id : undefined;
    const hasMore = docs.length === limit;
    return { documents: docs, lastId, hasMore };
  } catch (error) {
    console.error('Error fetching class announcements:', error);
    throw error;
  }
};

export const addClassAnnouncement = async (message: string, level: Level, img?: string) => {
  try {
    await ensureAuth();
    const data: { message: string; level: Level; time: string; img?: string } = {
      message,
      level,
      time: new Date().toISOString(),
    };
    if (img) data.img = img;
    const response = await databases.createDocument(
      DATABASE_ID,
      CLASS_ANNOUNCEMENTS_COLLECTION_ID,
      ID.unique(),
      data
    );
    return response;
  } catch (error) {
    console.error('Error adding class announcement:', error);
    throw error;
  }
};

export const uploadAnnouncementImage = async (file: File): Promise<string> => {
  try {
    await ensureAuth();
    const fileId = ID.unique();
    const upload = await cloudStorage.createFile(BUCKET_ID, fileId, file);
    return cloudStorage.getFileView(BUCKET_ID, upload.$id).toString();
  } catch (error) {
    console.error('Error uploading announcement image:', error);
    throw error;
  }
};

export const deleteClassAnnouncement = async (announcementId: string) => {
  try {
    await ensureAuth();
    await databases.deleteDocument(DATABASE_ID, CLASS_ANNOUNCEMENTS_COLLECTION_ID, announcementId);
  } catch (error) {
    console.error('Error deleting class announcement:', error);
    throw error;
  }
};
